/**
 * @file Provides the create and delete operations for an investment's external funding transactions.
 */
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { getIsoDateFromDate } from "@components/Date";
import {
  deleteExternalFundingTransactionOperation as deleteOperation,
  registerExternalFundingTransactionOperation as createOperation,
} from "@api/services/transactions";

import type { EditableTransaction } from "./types";
import type { CollectionRef, ReadInvestment } from "../../../types";

type FormItem = {
  amount?: number;
  description?: string;
};

/**
 * Provides functions to create and delete an investment's external funding transactions.
 */
const useTransactionOperations = (
  collection: CollectionRef,
  investmentId: string,
  readInvestment: ReadInvestment,
) => {
  const { request } = useAppContext() as AppContext;

  /**
   * Deletes an external funding transaction and reloads the investment.
   */
  const deleteTransaction = ({ id }: EditableTransaction) =>
    request(deleteOperation, { id })
      .then(() => readInvestment(investmentId));

  /**
   * Registers a new external funding transaction from the editable row's form values.
   */
  const createTransaction = async () => {
    const items: Record<string, FormItem> = collection.current.form.getFieldsValue();

    const [ key ] = Object.keys(items);
    const item = items[key];

    const {
      amount,
      description,
    } = item;

    const date = getIsoDateFromDate(new Date());

    const mutation = {
      date,
      amount,
      description,
      investmentId,
    };

    return request(createOperation, { mutation })
      .then(() => readInvestment(investmentId));
  };

  return { deleteTransaction, createTransaction };
};

export default useTransactionOperations;
